import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ShippingOptionType } from '../../../bff-api/baskets/model';
import { useEstimatedDeliveryDate } from '../../../bff-api/delivery/useEstimatedDeliveryDate';
import { isNotAvailableStatus, isNotEligibleStatus } from '../../../bff-api/utils/store-utils';
import { usePDP } from '../../../contexts/pdp/usePDP';
import { useSameDayDelivery } from '../../../hooks/useSameDayDelivery';
import { useUserZipCode } from '../../../hooks/useUserZipCode';
import { useCustomPreferenceOrDefault } from '../../../utils/api';
import { CustomPreference } from '../../../utils/api/CustomPreference';

const { SHIP_TO, BOPIS, SDD } = ShippingOptionType;

const notAvailableMessage = {
    id: 'delivery.options.not.available',
    defaultMessage: 'Not available',
};

interface DeliveryOptionDescriptions {
    [SHIP_TO]?: string;
    [BOPIS]?: string;
    [SDD]?: string;
}

export const useDeliveryOptionDescriptions = (): DeliveryOptionDescriptions => {
    const intl = useIntl();
    const {
        product,
        inventoryStore: { inventories, flags },
        isLoadingProductInventory,
        isLoadingBopisInventory,
        isLoadingSDDInventory,
    } = usePDP();
    const shipToInventory = inventories[SHIP_TO];
    const bopisInventory = inventories[BOPIS];
    const sddInventory = inventories[SDD];

    const { isNextDayDelivery } = useSameDayDelivery();
    const eddEnable = useCustomPreferenceOrDefault(CustomPreference.EDD_ENABLE, true);
    const standardShippingDescription = useCustomPreferenceOrDefault<string>(
        CustomPreference.DEFAULT_EDD_MESSAGE,
        '',
    );

    const { data: zipCode = '' } = useUserZipCode();
    const { data = {} } = useEstimatedDeliveryDate(zipCode);
    const { edd } = data;

    const memo = useMemo<DeliveryOptionDescriptions>((): DeliveryOptionDescriptions => {
        if (!product || isLoadingProductInventory) {
            return {
                [SHIP_TO]: undefined,
                [BOPIS]: undefined,
                [SDD]: undefined,
            };
        }

        let shipToDescription: string | undefined;
        let bopisDescription: string | undefined;
        let sddDescription: string | undefined;

        const {
            c_shippingOptions,
            c_dropShipEligibility,
            c_dropshipEstimatedDeliveryDate,
            c_dropshipEstimatedProcessingDate,
        } = product;
        const shipTo = c_shippingOptions.find(({ type }) => type === SHIP_TO)!;
        const bopis = c_shippingOptions.find(({ type }) => type === BOPIS)!;
        const sdd = c_shippingOptions.find(({ type }) => type === SDD)!;

        if (!shipTo.enabled || isNotAvailableStatus(shipToInventory.status)) {
            shipToDescription = intl.formatMessage(notAvailableMessage);
        } else if (c_dropShipEligibility) {
            if (c_dropshipEstimatedProcessingDate) {
                shipToDescription = intl.formatMessage(
                    {
                        id: 'delivery.options.arrives.in.date.processing',
                        defaultMessage: 'Ships in {date}',
                    },
                    {
                        date: c_dropshipEstimatedProcessingDate,
                    },
                );
            }

            if (c_dropshipEstimatedDeliveryDate) {
                shipToDescription = intl.formatMessage(
                    {
                        id: 'delivery.options.arrives.in.date',
                        defaultMessage: 'Arrives in {date}',
                    },
                    {
                        date: c_dropshipEstimatedDeliveryDate,
                    },
                );
            }
        } else if (flags.includesItemsInBackorder) {
            shipToDescription = intl.formatMessage({
                id: 'delivery.options.on.back.order',
                defaultMessage: 'On Backorder',
            });
        } else if (!eddEnable) {
            shipToDescription = standardShippingDescription;
        } else if (edd) {
            shipToDescription = intl.formatMessage(
                {
                    id: 'delivery.options.arrives.in.edd',
                    defaultMessage: 'Arrives in {edd}',
                },
                {
                    edd: `${edd} days`,
                },
            );
        }

        if (!shipToDescription?.trim()) {
            shipToDescription = standardShippingDescription;
        }

        if (
            !bopis.enabled ||
            (!isLoadingBopisInventory && isNotEligibleStatus(bopisInventory.status))
        ) {
            bopisDescription = intl.formatMessage(notAvailableMessage);
        } else {
            bopisDescription = intl.formatMessage({
                id: 'delivery.options.as.soon.as.today',
                defaultMessage: 'As soon as Today',
            });
        }

        if (!sdd.enabled || (!isLoadingSDDInventory && isNotEligibleStatus(sddInventory.status))) {
            sddDescription = intl.formatMessage(notAvailableMessage);
        } else if (isNextDayDelivery) {
            sddDescription = intl.formatMessage({
                id: 'delivery.options.arrives.tomorrow',
                defaultMessage: 'Arrives Tomorrow',
            });
        }
        if (!sddDescription) {
            sddDescription = intl.formatMessage({
                id: 'delivery.options.arrives.today',
                defaultMessage: 'Arrives Today',
            });
        }

        return {
            [SHIP_TO]: shipToDescription,
            [BOPIS]: bopisDescription,
            [SDD]: sddDescription,
        };
    }, [
        intl,
        flags,
        product,
        edd,
        shipToInventory,
        bopisInventory,
        sddInventory,
        isNextDayDelivery,
        isLoadingProductInventory,
        isLoadingBopisInventory,
        isLoadingSDDInventory,
        eddEnable,
        standardShippingDescription,
    ]);

    return memo;
};
